import React, { useEffect, useRef, useState } from "react"
import PlacesAutocomplete from "react-google-places-autocomplete"
import { useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { getPlaceDetailsByPlaceId } from "../../../../utils/placeDetails"
import { isSameLocation } from "../../../../utils/locationValidation"
import { getDefaultOptions, getDefaultOptionsSync } from "../../../../utils/defaultCityOptions"
import { saveRecentSearch } from "../../../../utils/recentSearches"
import { DatePickerInput, TimePickerInput, DateTimeProvider } from "../../../../components/DateTime"
import { useMapsReady } from "../../../../hooks/useMapsReady"

const formatShortDate = (dateStr) => {
  if (!dateStr) return null
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" })
}

const formatShortTime = (timeStr) => {
  if (!timeStr) return null
  const [h, m] = timeStr.split(":").map(Number)
  if (isNaN(h)) return timeStr
  const ampm = h >= 12 ? "PM" : "AM"
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`
}

const truncate = (str, n = 18) => (str && str.length > n ? str.slice(0, n) + "…" : str)

const panelPlacesStyles = {
  container: (p) => ({ ...p, width: "100%" }),
  control: (provided) => ({
    ...provided,
    border: "1.5px solid #e0e0e0",
    boxShadow: "none",
    width: "100%",
    height: "44px",
    minHeight: "44px",
    background: "#fff",
    fontSize: "1.3rem",
    color: "#333",
    borderRadius: "0.6rem",
  }),
  valueContainer: (p) => ({ ...p, height: "44px", padding: "0 10px" }),
  input: (p) => ({ ...p, color: "#333", fontSize: "1.3rem", margin: 0, padding: 0 }),
  placeholder: (p) => ({ ...p, color: "#aaa", fontSize: "1.3rem" }),
  singleValue: (p) => ({ ...p, color: "#333" }),
  menu: (p) => ({ ...p, fontSize: "1.3rem", zIndex: 99999 }),
}

const UpdateLocalForm = () => {
  const [formValues, setFormValues] = useState({
    from: null,
    to: null,
    pickUpDate: "",
    pickUpTime: "",
    mobileNumber: "",
  })

  const { location } = useSelector((state) => state.location)
  const navigate = useNavigate()
  const params = useParams()
  const [mobileExpanded, setMobileExpanded] = useState(false)
  const mapsReady = useMapsReady()
  const [defaultFromOptions, setDefaultFromOptions] = useState(() =>
    getDefaultOptionsSync("from")
  )
  const [defaultToOptions, setDefaultToOptions] = useState(() =>
    getDefaultOptionsSync("to")
  )
  const [fromMenuIsOpen, setFromMenuIsOpen] = useState(false)
  const [toMenuIsOpen, setToMenuIsOpen] = useState(false)

  const pickUpDateRef = useRef(null)
  const pickUpTimeRef = useRef(null)

  const today = new Date()
  const formattedToday = today.toLocaleDateString("en-CA")

  const loadFormValues = () => {
    const storedTripDataStr = localStorage.getItem("tripData")
    if (!storedTripDataStr) return
    const tripData = JSON.parse(storedTripDataStr)

    const convertStringToPlaceFormat = (stringValue, lat, lng, city) => {
      if (!stringValue) return null
      if (typeof stringValue === "string") {
        return {
          label: stringValue,
          value: { description: stringValue, place_id: `temp_${Date.now()}_${Math.random()}` },
          latitude: lat || null,
          longitude: lng || null,
          city: city || "",
          fullAddress: stringValue,
        }
      } else if (stringValue?.label && stringValue?.value) {
        return stringValue
      }
      return null
    }

    setFormValues({
      from: tripData.pickup ? convertStringToPlaceFormat(tripData.pickup, tripData.pickup_lat, tripData.pickup_long, tripData.pickup_city) : null,
      to: tripData.drop ? convertStringToPlaceFormat(tripData.drop, tripData.drop_lat, tripData.drop_long, tripData.drop_city) : null,
      pickUpDate: tripData.trip_date || "",
      pickUpTime: tripData.trip_time || "",
      mobileNumber: tripData.phone_number || "",
    })
  }

  useEffect(() => {
    loadFormValues()
    const handleStorageChange = () => loadFormValues()
    window.addEventListener("storage", handleStorageChange)
    window.addEventListener("tripDataUpdated", handleStorageChange)
    return () => {
      window.removeEventListener("storage", handleStorageChange)
      window.removeEventListener("tripDataUpdated", handleStorageChange)
    }
  }, [params.fromCity, params.toCity])

  useEffect(() => {
    const loadDefaultOptions = async () => {
      const currentCity = location?.city || formValues?.from?.city || formValues?.from?.label
      const fromCity = formValues?.from?.city || formValues?.from?.label
      setDefaultFromOptions(await getDefaultOptions("from", currentCity) || [])
      setDefaultToOptions(await getDefaultOptions("to", null, fromCity || currentCity) || [])
    }
    loadDefaultOptions()
  }, [location?.city, formValues?.from])

  const handlePlaceSelect = async (value, field) => {
    if (!value) { setFormValues((prev) => ({ ...prev, [field]: null })); return }
    if (field === "to" && formValues.from && isSameLocation(value, formValues.from)) {
      setFormValues((prev) => ({ ...prev, to: null }))
      const storedTrip = JSON.parse(localStorage.getItem("tripData") || "{}")
      delete storedTrip.drop; delete storedTrip.drop_lat; delete storedTrip.drop_long; delete storedTrip.drop_city
      localStorage.setItem("tripData", JSON.stringify(storedTrip))
      toast.error("Pickup and drop locations cannot be the same")
      return
    }
    try {
      const placeId = value?.value?.place_id || value?.place_id
      const details = placeId ? await getPlaceDetailsByPlaceId(placeId) : null
      if (!details) return
      const nextValue = { ...value, latitude: details.lat, longitude: details.lng, city: details.city, fullAddress: details.fullAddress }
      setFormValues((prev) => ({ ...prev, [field]: nextValue }))
      saveRecentSearch(nextValue, field === "from" ? "from" : "to")
      if (field === "from") {
        setDefaultToOptions(await getDefaultOptions("to", null, details.city) || [])
        setToMenuIsOpen(true)
      } else {
        setTimeout(() => { pickUpDateRef.current?.open?.() }, 150)
      }
    } catch (error) {
      console.error("Error fetching location details:", error)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formValues.from) return toast.error("Please select a pickup location")
    if (!formValues.to) return toast.error("Please select a drop location")
    if (isSameLocation(formValues.from, formValues.to)) return toast.error("Pickup and drop locations cannot be the same")
    if (!formValues.pickUpDate) return toast.error("Please select pickup date")
    if (!formValues.pickUpTime) return toast.error("Please select pickup time")

    const parsedTripData = JSON.parse(localStorage.getItem("tripData") || "{}")
    const getFullAddress = (v) => (v ? (typeof v === "string" ? v : v.fullAddress || v.label || "") : "")
    const phoneFromUrl = new URLSearchParams(window.location.search).get("phone_number") || ""

    const tripData = {
      trip_type: 1,
      round_trip: 0,
      pickup: getFullAddress(formValues.from) || parsedTripData?.pickup || "",
      drop: getFullAddress(formValues.to) || parsedTripData?.drop || "",
      pickup_lat: formValues.from?.latitude || parsedTripData?.pickup_lat || null,
      pickup_long: formValues.from?.longitude || parsedTripData?.pickup_long || null,
      drop_lat: formValues.to?.latitude || parsedTripData?.drop_lat || null,
      drop_long: formValues.to?.longitude || parsedTripData?.drop_long || null,
      pickup_city: formValues.from?.city || parsedTripData?.pickup_city || "",
      drop_city: formValues.to?.city || parsedTripData?.drop_city || "",
      package_type_in_hours: "",
      trip_date: formValues.pickUpDate,
      trip_time: formValues.pickUpTime,
      phone_number: formValues.mobileNumber || parsedTripData?.phone_number || phoneFromUrl || "",
      ip_address: location?.ip || "8425",
    }

    localStorage.setItem("tripData", JSON.stringify(tripData))

    const buildParam = (key, value) => (value !== null && value !== undefined && value !== "" ? `${key}=${encodeURIComponent(value)}` : "")
    const queryParams = [
      `trip_type=${tripData.trip_type}`,
      `round_trip=${tripData.round_trip}`,
      buildParam("pickup", tripData.pickup),
      buildParam("drop", tripData.drop),
      buildParam("pickup_lat", tripData.pickup_lat),
      buildParam("pickup_long", tripData.pickup_long),
      buildParam("drop_lat", tripData.drop_lat),
      buildParam("drop_long", tripData.drop_long),
      buildParam("pickup_city", tripData.pickup_city),
      buildParam("drop_city", tripData.drop_city),
      buildParam("trip_date", tripData.trip_date),
      buildParam("trip_time", tripData.trip_time),
      buildParam("phone_number", tripData.phone_number),
    ].filter(Boolean)

    navigate(`/cabs?${queryParams.join("&")}`)
  }

  const fromLabel = formValues.from?.label || "—"
  const toLabel = formValues.to?.label || "—"
  const pickupDateShort = formatShortDate(formValues.pickUpDate)
  const pickupTimeShort = formatShortTime(formValues.pickUpTime)
  const placesComponents = { DropdownIndicator: () => null, IndicatorSeparator: () => null, ClearIndicator: () => null }

  return (
    <div className="updateLocalForm">
      {/* ── BAR (always visible) ── */}
      <div className="updateLocalForm__bar">
        <span className="updateLocalForm__bar-badge">Local</span>

        <div className="updateLocalForm__bar-chip">
          <span className="updateLocalForm__bar-chipLabel">From</span>
          <span className="updateLocalForm__bar-chipValue">{truncate(fromLabel, 20)}</span>
        </div>

        <span className="updateLocalForm__bar-arrow">→</span>

        <div className="updateLocalForm__bar-chip">
          <span className="updateLocalForm__bar-chipLabel">To</span>
          <span className="updateLocalForm__bar-chipValue">{truncate(toLabel, 20)}</span>
        </div>

        <div className="updateLocalForm__bar-chip">
          <span className="updateLocalForm__bar-chipLabel">Date</span>
          <span className="updateLocalForm__bar-chipValue">{pickupDateShort || "—"}</span>
        </div>

        <div className="updateLocalForm__bar-chip updateLocalForm__bar-chip--time">
          <span className="updateLocalForm__bar-chipLabel">Time</span>
          <span className="updateLocalForm__bar-chipValue">{pickupTimeShort || "—"}</span>
        </div>

        <button
          className="updateLocalForm__bar-modifyBtn"
          onClick={() => setMobileExpanded((v) => !v)}
          type="button"
        >
          {mobileExpanded ? (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
              Close
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Modify
            </>
          )}
        </button>
      </div>

      {/* ── PANEL (slides down on Modify) ── */}
      <div className={`updateLocalForm__panel${mobileExpanded ? " updateLocalForm__panel--open" : ""}`}>
        <form onSubmit={handleSubmit} className="updateLocalForm__container">
          <div className="updateLocalForm__field updateLocalForm__field--from">
            <label className="field-label">From</label>
            {mapsReady && <PlacesAutocomplete
              selectProps={{
                value: formValues.from || null,
                onChange: (v) => { handlePlaceSelect(v, "from"); setFromMenuIsOpen(false) },
                placeholder: "From *",
                isClearable: true,
                defaultOptions: defaultFromOptions,
                menuIsOpen: fromMenuIsOpen,
                onFocus: () => setFromMenuIsOpen(true),
                onBlur: () => setFromMenuIsOpen(false),
                onMenuClose: () => setFromMenuIsOpen(false),
                getOptionLabel: (e) => (e && e.label) || "",
                getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
                components: placesComponents,
                styles: panelPlacesStyles,
              }}
              autocompletionRequest={{ componentRestrictions: { country: "in" } }}
            />}
          </div>

          <div className="updateLocalForm__field updateLocalForm__field--to">
            <label className="field-label">To</label>
            {mapsReady && <PlacesAutocomplete
              debounce={400}
              minLengthAutocomplete={3}
              selectProps={{
                value: formValues.to || null,
                onChange: (v) => { handlePlaceSelect(v, "to"); setToMenuIsOpen(false) },
                placeholder: "To *",
                isClearable: true,
                defaultOptions: defaultToOptions,
                menuIsOpen: toMenuIsOpen,
                onFocus: () => setToMenuIsOpen(true),
                onBlur: () => setToMenuIsOpen(false),
                onMenuClose: () => setToMenuIsOpen(false),
                getOptionLabel: (e) => (e && e.label) || "",
                getOptionValue: (e) => e?.value?.place_id || e?.place_id || e?.value?.description || e?.label || "",
                components: placesComponents,
                styles: panelPlacesStyles,
              }}
              autocompletionRequest={{ componentRestrictions: { country: "in" } }}
            />}
          </div>

          <DateTimeProvider>
            <div className="updateLocalForm__field updateLocalForm__field--date dtp-white">
              <label className="field-label">Pickup Date</label>
              <DatePickerInput
                ref={pickUpDateRef}
                value={formValues.pickUpDate ? new Date(formValues.pickUpDate) : null}
                minDate={new Date(formattedToday)}
                placeholder="Pick Up Date *"
                onChange={(d) => {
                  setFormValues((prev) => ({ ...prev, pickUpDate: d ? new Date(d).toLocaleDateString("en-CA") : "" }))
                  setTimeout(() => { pickUpTimeRef.current?.open?.() }, 200)
                }}
              />
            </div>
            <div className="updateLocalForm__field updateLocalForm__field--time ttp-white">
              <label className="field-label">Pickup Time</label>
              <TimePickerInput
                ref={pickUpTimeRef}
                value={formValues.pickUpTime}
                placeholder="Pick Up Time *"
                onChange={(t) => setFormValues((prev) => ({ ...prev, pickUpTime: t || "" }))}
              />
            </div>
          </DateTimeProvider>

          <div className="updateLocalForm__field updateLocalForm__field--submit">
            <label className="field-label">&nbsp;</label>
            <button type="submit" className="updateLocalForm__submitBtn">Search</button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default UpdateLocalForm
